"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Calendar, Banknote, Check } from "lucide-react";

export default function PromoterPayoutScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Commission ledger", "Libro de comisiones")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("You pay. ", "Tú pagas. ")}
            <span className="text-white/30">{t("Redy keeps the record.", "Redy guarda el registro.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Redy calculates what each promoter earned. You pay them your way, then mark their commissions paid in Redy. No more \"I think you owe me $X\" disputes — the number is on the record.",
              "Redy calcula lo que ganó cada promotor. Les pagas como prefieras y marcas sus comisiones pagadas en Redy. Sin más \"creo que me debes $X\" — el número queda en el registro."
            )}
          </p>
        </motion.div>

        {/* Payout flow mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-7 md:p-8 max-w-[720px] mx-auto"
        >
          {/* Friday → Monday timeline */}
          <div className="flex items-center justify-between mb-8 text-[10px] font-bold uppercase tracking-widest text-white/45">
            <div className="text-center">
              <div className="text-[16px] font-black text-white mb-1">Fri</div>
              <div>{t("Sales close", "Cierre")}</div>
            </div>
            <div className="flex-1 mx-3 h-px relative">
              <motion.div
                initial={{ width: "0%" }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.5, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="absolute inset-y-0 left-0"
                style={{
                  background: "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)",
                }}
              />
            </div>
            <div className="text-center">
              <div className="text-[16px] font-black text-white mb-1">Sat</div>
              <div>{t("Review", "Revisar")}</div>
            </div>
            <div className="flex-1 mx-3 h-px relative">
              <motion.div
                initial={{ width: "0%" }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.5, delay: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="absolute inset-y-0 left-0"
                style={{
                  background: "linear-gradient(90deg, #F472B6 0%, #FB923C 100%)",
                }}
              />
            </div>
            <div className="text-center">
              <div className="text-[16px] font-black text-white mb-1">Mon</div>
              <div style={{ color: "#F472B6" }}>{t("Mark paid", "Marcar pagado")}</div>
            </div>
          </div>

          {/* Sofia's payout card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="rounded-[20px] p-6 text-white relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 50px rgba(124,58,237,0.35)",
            }}
          >
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 25% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)",
              }}
            />
            <div className="relative">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-white/75">
                    {t("Commission", "Comisión")}
                  </div>
                  <div className="text-[18px] font-black">Sofia Reyes</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-white/75">
                    {t("Status", "Estado")}
                  </div>
                  <div className="text-[12px] font-black">{t("Marked paid", "Marcada pagada")}</div>
                </div>
              </div>

              <div className="bg-black/30 backdrop-blur-sm rounded-[14px] p-4 mb-4">
                <div className="text-[9px] font-bold uppercase tracking-widest text-white/65 mb-1">
                  {t("Amount", "Monto")}
                </div>
                <div className="text-[48px] font-black tabular-nums leading-none">$522.00</div>
                <div className="text-[10px] font-bold mt-1 text-white/75">
                  {t("15% of $3,480 in tickets", "15% de $3,480 en tickets")}
                </div>
              </div>

              <div className="flex items-center gap-2 text-[11px] font-bold">
                <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                  <Check className="w-3 h-3" style={{ color: "#2ECC71" }} strokeWidth={3.5} />
                </div>
                <span className="text-white">
                  {t("Paid by the owner · 87 tickets attributed · 15% commission rate", "Pagada por el dueño · 87 tickets atribuidos · 15% comisión")}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Caption */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="mt-6 flex items-center justify-center gap-2 text-[12px] font-bold text-white/65"
          >
            <Banknote className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
            <span>
              {t("12 promoters settled · $4,287 in commissions · every one on the record.", "12 promotores liquidados · $4,287 en comisiones · cada una en el registro.")}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
